// ABOUTME: Form panel for editing Level 5 global default configuration values.
// ABOUTME: Shows key-value pairs for default_batch_size, default_preparation_minutes, etc.

import React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Checkbox from '@mui/material/Checkbox';
import { useTranslation } from 'react-i18next';
import { colors } from '../../theme/colors';

const DEFAULT_FIELDS = [
  { key: 'default_batch_size', type: 'number' },
  { key: 'default_preparation_minutes', type: 'number' },
  { key: 'default_baking_minutes', type: 'number' },
  { key: 'default_unloading_minutes', type: 'number' },
  { key: 'default_prediction_horizon_minutes', type: 'number' },
  { key: 'default_price', type: 'number' },
  { key: 'default_category', type: 'text' },
  { key: 'min_required_transactions', type: 'number', hasInfo: true },
  { key: 'update_interval', type: 'number', hasInfo: true },
  { key: 'zero_next_day_stocks', type: 'checkbox', hasInfo: true },
];

export default function GlobalDefaultsPanel({ defaults, pendingChanges, onFieldChange }) {
  const { t } = useTranslation();

  const getPendingValue = (key) => {
    const change = pendingChanges.find(
      c => c.level === 5 && c.field === key
    );
    return change ? change.value : undefined;
  };

  return (
    <Paper elevation={0} sx={{
      border: `1px solid ${colors.border.light}`,
      borderRadius: '12px',
      p: 3,
      maxWidth: 600,
    }}>
      <Typography sx={{
        fontSize: '1rem',
        fontWeight: 600,
        color: colors.text.primary,
        mb: 2,
      }}>
        {t('config.tabs.defaults')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {DEFAULT_FIELDS.map(field => {
          const currentValue = defaults?.[field.key];
          const pendingValue = getPendingValue(field.key);
          const isPending = pendingValue !== undefined;
          const displayValue = isPending ? pendingValue : (currentValue ?? '');

          return (
            <Box key={field.key} sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              ...(isPending ? {
                backgroundColor: '#FFF7ED',
                borderLeft: `3px solid ${colors.palette.orange}`,
                pl: 1,
                borderRadius: '4px',
              } : {}),
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 260 }}>
                <Typography sx={{
                  fontSize: '0.85rem',
                  color: colors.text.secondary,
                }}>
                  {t(`config.defaultFields.${field.key}`)}
                </Typography>
                {field.hasInfo && (
                  <Tooltip title={t(`config.defaultFieldInfo.${field.key}`)} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: '0.95rem', color: colors.text.disabled, cursor: 'help' }} />
                  </Tooltip>
                )}
              </Box>
              {field.type === 'checkbox' ? (
                <Checkbox
                  checked={!!displayValue}
                  onChange={(e) => {
                    onFieldChange(field.key, e.target.checked, currentValue);
                  }}
                  size="small"
                />
              ) : (
                <TextField
                  size="small"
                  type={field.type}
                  value={displayValue}
                  onChange={(e) => {
                    const val = field.type === 'number'
                      ? (e.target.value === '' ? '' : Number(e.target.value))
                      : e.target.value;
                    onFieldChange(field.key, val, currentValue);
                  }}
                  sx={{
                    width: 160,
                    '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}
